// Copyright 2026 Granite (granite.md). All rights reserved.
// Proprietary and confidential. Unauthorized copying is prohibited.

/**
 * Right-click on a vault in the vault lists (Home and the sidebar): rename and delete it
 * (PLAN.md P2-T01), and for a workspace vault, who can open all of it (P8-T03). The server
 * decides who may (vault admins; a personal vault's owner always): a refusal shows in the dialog.
 */
import { unwrap } from "@granite/api-client";
import { Button, Dialog, DialogContent, TextField } from "@granite/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useRouter } from "@tanstack/react-router";
import { ContextMenu } from "radix-ui";
import { useState, type ReactElement } from "react";

import { api } from "../lib/api.js";
import { tell } from "../lib/dialogs.js";
import { errorMessage, fieldErrors } from "../lib/errors.js";
import { t } from "../lib/i18n.js";
import { isDesktop } from "../lib/platform.js";
import type { Vault } from "../lib/vaults.js";
import { FormAlert } from "./AuthLayout.js";
import { ContextMenuItem, contextMenuContentClass, ContextMenuSeparator } from "./contextMenu.js";
import { VaultAccessDialog } from "./VaultAccessDialog.js";

type Open = "rename" | "access" | "delete" | null;

/** Wraps a vault's row (a link) with its right-click menu. */
export function VaultMenu({ vault, children }: { vault: Vault; children: ReactElement }) {
  const [open, setOpen] = useState<Open>(null);
  const workspaces = useQuery({
    queryKey: ["workspaces"],
    enabled: Boolean(vault.workspace_id),
    queryFn: async () => unwrap(await api.GET("/v1/workspaces")),
  });
  // Workspace guests only read and comment: nothing here is theirs to do.
  const role = workspaces.data?.items.find((w) => w.id === vault.workspace_id)?.role;
  if (vault.workspace_id && role === "guest") return children;
  const close = () => {
    setOpen(null);
  };
  return (
    <>
      <ContextMenu.Root>
        <ContextMenu.Trigger asChild>{children}</ContextMenu.Trigger>
        <ContextMenu.Portal>
          <ContextMenu.Content className={contextMenuContentClass}>
            <ContextMenuItem onSelect={() => { setOpen("rename"); }}>{t("vaultMenu.rename")}</ContextMenuItem>
            {vault.workspace_id && <ContextMenuItem onSelect={() => { setOpen("access"); }}>{t("access.info.manage")}</ContextMenuItem>}
            <ContextMenuSeparator />
            <ContextMenuItem onSelect={() => { setOpen("delete"); }}>{t("vaultMenu.delete")}</ContextMenuItem>
          </ContextMenu.Content>
        </ContextMenu.Portal>
      </ContextMenu.Root>
      {open === "rename" && <RenameVaultDialog vault={vault} onClose={close} />}
      {open === "access" && <VaultAccessDialog vault={vault} onClose={close} />}
      {open === "delete" && <DeleteVaultDialog vault={vault} onClose={close} />}
    </>
  );
}

function RenameVaultDialog({ vault, onClose }: { vault: Vault; onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState(vault.name);
  const save = useMutation({
    mutationFn: async () => {
      if (name.trim() === vault.name) return;
      unwrap(await api.PATCH("/v1/vaults/{vault_id}", { params: { path: { vault_id: vault.id } }, body: { name: name.trim() } }));
      await qc.invalidateQueries({ queryKey: ["vaults"] });
      await qc.invalidateQueries({ queryKey: ["vault", vault.id] });
    },
    onSuccess: onClose,
  });
  const fields = fieldErrors(save.error);
  return (
    <Dialog
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent title={t("vaultMenu.renameTitle")}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim()) save.mutate();
          }}
          className="flex flex-col gap-4"
        >
          {save.error && !fields.name && <FormAlert tone="error">{errorMessage(save.error)}</FormAlert>}
          <TextField
            label={t("vaultUi.settings.vaultName")}
            value={name}
            maxLength={100}
            autoFocus
            error={fields.name}
            onChange={(e) => {
              setName(e.target.value);
            }}
          />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={onClose}>
              {t("vaultUi.settings.cancel")}
            </Button>
            <Button type="submit" disabled={!name.trim()} loading={save.isPending}>
              {t("vaultUi.settings.save")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Moves the vault to the server's trash. In the desktop app its folder in ~/Documents/Granite
 * goes to the system Trash too (P4-T09), or it would stay linked to a deleted vault.
 */
function DeleteVaultDialog({ vault, onClose }: { vault: Vault; onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const router = useRouter();
  // Away from the vault first when it is the one open.
  const leave = async (at: string) => {
    const path = router.state.location.pathname;
    if (path === at || path.startsWith(`${at}/`)) await navigate({ to: "/", search: {} });
  };
  const del = useMutation({
    mutationFn: async () => {
      unwrap(await api.DELETE("/v1/vaults/{vault_id}", { params: { path: { vault_id: vault.id } } }));
      await leave(`/vault/${vault.id}`);
      if (isDesktop) {
        try {
          const folders = await import("../platform/accountFolders.js");
          await folders.trashAccountFolder(vault.id, (localId) => leave(`/local/${localId}`));
        } catch (err) {
          await tell(t("vaultMenu.folderFailed", { error: String(err) }));
        }
      }
    },
    onSuccess: async () => {
      qc.removeQueries({ queryKey: ["vault", vault.id] });
      await qc.invalidateQueries({ queryKey: ["vaults"] });
      onClose();
    },
  });
  return (
    <Dialog
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent
        title={t("vaultMenu.deleteTitle", { name: vault.name })}
        description={isDesktop ? `${t("vaultMenu.deleteDescription")} ${t("vaultMenu.deleteFolder")}` : t("vaultMenu.deleteDescription")}
        footer={
          <>
            <Button variant="secondary" onClick={onClose} autoFocus>
              {t("vaultUi.dialogs.cancel")}
            </Button>
            <Button
              variant="danger"
              loading={del.isPending}
              onClick={() => {
                del.mutate();
              }}
            >
              {t("vaultMenu.deleteConfirm")}
            </Button>
          </>
        }
      >
        {del.error && <FormAlert tone="error">{errorMessage(del.error)}</FormAlert>}
      </DialogContent>
    </Dialog>
  );
}
